/**
 * Browser detection utilities for handling browser-specific behaviors and restrictions
 */

export interface BrowserInfo {
  isTikTokInApp: boolean;
  isInstagramInApp: boolean;
  isFacebookInApp: boolean;
  isTwitterInApp: boolean;
  isLinkedInInApp: boolean;
  isMobile: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  browserName: string;
  browserVersion: string;
  hasScriptRestrictions: boolean;
}

/**
 * Detects if the current browser is TikTok's in-app browser
 */
export const isTikTokInAppBrowser = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent.toLowerCase();
  return userAgent.includes('tiktok') || userAgent.includes('bytedance');
};

/**
 * Detects if the current browser is Instagram's in-app browser
 */
export const isInstagramInAppBrowser = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent.toLowerCase();
  return userAgent.includes('instagram');
};

/**
 * Detects if the current browser is Facebook's in-app browser
 */
export const isFacebookInAppBrowser = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent.toLowerCase();
  return userAgent.includes('fban') || userAgent.includes('fbav') || userAgent.includes('fbios');
};

/**
 * Detects if the current browser is Twitter's in-app browser
 */
export const isTwitterInAppBrowser = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent.toLowerCase();
  return userAgent.includes('twitter');
};

/**
 * Detects if the current browser is LinkedIn's in-app browser
 */
export const isLinkedInInAppBrowser = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent.toLowerCase();
  return userAgent.includes('linkedin');
};

/**
 * Detects if the browser has known script execution restrictions
 */
export const hasScriptRestrictions = (): boolean => {
  return (
    isTikTokInAppBrowser() ||
    isInstagramInAppBrowser() ||
    isFacebookInAppBrowser() ||
    isTwitterInAppBrowser() ||
    isLinkedInInAppBrowser()
  );
};

/**
 * Gets comprehensive browser information
 */
export const getBrowserInfo = (): BrowserInfo => {
  if (typeof window === 'undefined') {
    return {
      isTikTokInApp: false,
      isInstagramInApp: false,
      isFacebookInApp: false,
      isTwitterInApp: false,
      isLinkedInInApp: false,
      isMobile: false,
      isIOS: false,
      isAndroid: false,
      browserName: 'unknown',
      browserVersion: 'unknown',
      hasScriptRestrictions: false,
    };
  }

  const { userAgent } = window.navigator;
  const userAgentLower = userAgent.toLowerCase();

  // Mobile detection
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  const isIOS = /ipad|iphone|ipod/.test(userAgentLower);
  const isAndroid = userAgentLower.includes('android');

  // Browser name detection
  let browserName = 'unknown';
  let browserVersion = 'unknown';

  if (userAgentLower.includes('tiktok')) {
    browserName = 'TikTok';
    const match = userAgent.match(/tiktok\s([\d.]+)/i);
    browserVersion = match ? match[1] : 'unknown';
  } else if (userAgentLower.includes('instagram')) {
    browserName = 'Instagram';
  } else if (userAgentLower.includes('fban') || userAgentLower.includes('fbav')) {
    browserName = 'Facebook';
  } else if (userAgentLower.includes('twitter')) {
    browserName = 'Twitter';
  } else if (userAgentLower.includes('linkedin')) {
    browserName = 'LinkedIn';
  } else if (userAgentLower.includes('chrome')) {
    browserName = 'Chrome';
    const match = userAgent.match(/chrome\/([\d.]+)/i);
    browserVersion = match ? match[1] : 'unknown';
  } else if (userAgentLower.includes('safari')) {
    browserName = 'Safari';
    const match = userAgent.match(/version\/([\d.]+)/i);
    browserVersion = match ? match[1] : 'unknown';
  } else if (userAgentLower.includes('firefox')) {
    browserName = 'Firefox';
    const match = userAgent.match(/firefox\/([\d.]+)/i);
    browserVersion = match ? match[1] : 'unknown';
  } else if (userAgentLower.includes('edge')) {
    browserName = 'Edge';
    const match = userAgent.match(/edg\/([\d.]+)/i);
    browserVersion = match ? match[1] : 'unknown';
  }

  return {
    isTikTokInApp: isTikTokInAppBrowser(),
    isInstagramInApp: isInstagramInAppBrowser(),
    isFacebookInApp: isFacebookInAppBrowser(),
    isTwitterInApp: isTwitterInAppBrowser(),
    isLinkedInInApp: isLinkedInInAppBrowser(),
    isMobile,
    isIOS,
    isAndroid,
    browserName,
    browserVersion,
    hasScriptRestrictions: hasScriptRestrictions(),
  };
};

/**
 * Build a deep link that breaks the current page out of an in-app browser
 * (Facebook / Instagram / TikTok / Twitter / LinkedIn) and re-opens it in the
 * device's default system browser.
 *
 * Strategy:
 *  - Android: use an `intent://` URL targeting Chrome's package — Android
 *    routes this through the system intent picker, which bypasses the
 *    embedding WebView and hands the URL to Chrome (or whichever browser the
 *    user has set as default if Chrome is missing, via `S.browser_fallback_url`).
 *  - iOS: Apple does not expose a public scheme that reliably hands a URL to
 *    Safari from inside another app's WKWebView. We return `null` so the
 *    banner falls back to a copy-friendly instruction ("Tap the ⋯ menu and
 *    choose Open in Safari"), which is the only path that actually works on
 *    iOS without a private scheme.
 *  - Anything else (desktop, true mobile browsers): also `null` — there is
 *    nothing to break out of.
 *
 * The returned href is safe to put in an `<a href>` directly. Returns `null`
 * when no escape link is meaningful for the current platform.
 */
export const buildOpenInDefaultBrowserHref = (currentUrl?: string): string | null => {
  if (typeof window === 'undefined') return null;
  const info = getBrowserInfo();
  if (!info.hasScriptRestrictions) return null;

  const target = currentUrl || window.location.href;

  if (info.isAndroid) {
    let withoutScheme = target.replace(/^https?:\/\//i, '');
    const fragmentIndex = withoutScheme.indexOf('#');
    let pageFragment = '';
    if (fragmentIndex !== -1) {
      pageFragment = withoutScheme.slice(fragmentIndex);
      withoutScheme = withoutScheme.slice(0, fragmentIndex);
    }
    const intentExtras = [
      'scheme=https',
      'package=com.android.chrome',
      `S.browser_fallback_url=${encodeURIComponent(target)}`,
      'end',
    ].join(';');
    return `intent://${withoutScheme}#Intent;${intentExtras};${pageFragment ? pageFragment.slice(1) : ''}`;
  }

  return null;
};

/**
 * Logs browser information for debugging purposes
 */
export const logBrowserInfo = (): void => {
  const browserInfo = getBrowserInfo();
  // eslint-disable-next-line no-console
  console.log('[Browser Detection]', browserInfo);

  if (browserInfo.hasScriptRestrictions) {
    // eslint-disable-next-line no-console
    console.warn(
      `[Browser Detection] Script restrictions detected in ${browserInfo.browserName}. Some features may not work as expected.`,
    );
  }
};
